/* --- Product-page behavior; loaded only when request.page_type is product --- */

const productThemeStrings = (window.KPTheme && window.KPTheme.strings) || {};

function findVariantByOptions(variants, selectedOptions) {
  // Shopify products with only the Default Title variant intentionally render
  // no option control. Some app/theme events still ask us to re-apply the
  // selection; treating the absent control as an invalid choice cleared both
  // form id inputs and made the next Add to Cart post without a variant (422).
  // A single variant is unambiguous, so keep it selected. Multi-variant
  // products still fail closed when an option is genuinely missing.
  if (!selectedOptions.length || selectedOptions.some((value) => value === undefined)) {
    return variants.length === 1 ? variants[0] : null;
  }
  return variants.find((variant) => {
    return variant.options.length === selectedOptions.length
      && variant.options.every((option, index) => option === selectedOptions[index]);
  }) || null;
}

/* --- Product Variants, Gallery & Sticky ATC --- */
function initProductDetails(root) {
  const scope = root || document;
  const productForm = scope.querySelector('.shopify-product-form');
  if (!productForm) return;
  if (productForm.dataset.productControllerInitialized === 'true') return;
  productForm.dataset.productControllerInitialized = 'true';

  const productRoot = productForm.closest('.pdp-info') || document;
  const variantIdInput = productForm.querySelector('input[name="id"]');
  const mainImage = document.querySelector('.pdp-gallery-main img');
  const thumbItems = document.querySelectorAll('.pdp-thumb-item');
  const priceDisplays = document.querySelectorAll('.pdp-price');
  const comparePriceDisplays = document.querySelectorAll('.pdp-compare-price .money');
  const priceContainer = document.querySelector('.pdp-price-box .f-price');
  const regularPriceGroup = priceContainer?.querySelector('.f-price__regular');
  const salePriceGroup = priceContainer?.querySelector('.f-price__sale');
  const stickyAtc = document.querySelector('.sticky-atc-bar');
  const mainAtcBtn = productForm.querySelector('button[type="submit"]');

  // Parse variant dataset if exists
  let variants = [];
  try {
    const dataEl = document.getElementById('variant-json-data');
    if (dataEl) variants = JSON.parse(dataEl.textContent);
  } catch (e) {
    console.error('Failed to parse variant JSON', e);
  }

  // Swiping & Thumbnail Sync for pdp-gallery-main
  const galleryMain = document.getElementById('pdp-gallery-main');
  const gallerySlides = galleryMain ? galleryMain.querySelectorAll('.pdp-main-image-item') : [];
  let galleryActiveIndex = 0;
  let lastGalleryRatio = null;
  let goToSlide = function () {};

  // Sizes the gallery to the active slide's real proportions, so images are
  // neither distorted nor letterboxed.
  //
  // Uses CSS aspect-ratio rather than a computed pixel height. That choice
  // matters: it removes an entire hazard class rather than defending against
  // it. An earlier px-height version had to measure a width to divide by,
  // and measuring width is what closed a runaway feedback loop that grew the
  // gallery to 4,397,180px (a bare `1fr` grid track expands to fit content,
  // so writing a height widened the track, which was then re-measured). This
  // version measures NOTHING, so that loop cannot form even if a future CSS
  // change reintroduces a growable track. It also needs no viewport clamp and
  // no resize handler — aspect-ratio recomputes itself against the element's
  // own width whenever the viewport changes.
  //
  // aspect-ratio genuinely works on this element. A previous comment here
  // claimed it "doesn't work in Chromium on scrollable flexboxes" — that was
  // false. A stale duplicate .pdp-gallery-main rule was declaring
  // `aspect-ratio: 1/1 !important` and winning the cascade, masking every
  // test. That block is deleted; aspect-ratio then applied at normal
  // importance (verified live: `3` produced 612x204).
  //
  // Slides with no data-aspect-ratio (the placeholder when a product has no
  // media) yield NaN and return early, leaving the CSS 1/1 fallback.
  function updateGalleryCounter(index) {
    const el = document.querySelector('.pdp-gallery-counter');
    if (!el || !gallerySlides.length) return;
    el.textContent = (index + 1) + ' / ' + gallerySlides.length;
  }

  function syncGalleryVideos(index) {
    gallerySlides.forEach((slide, i) => {
      const video = slide.querySelector('video');
      if (!video) return;
      if (i === index) {
        video.muted = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(function () {});
        }
      } else if (!video.paused) {
        video.pause();
        try { video.currentTime = 0; } catch (e) {}
      }
    });
  }

  const galleryWrap = galleryMain && galleryMain.parentElement;

  function applyGalleryRatioValue(ratio) {
    if (!galleryWrap || !ratio) return;
    if (lastGalleryRatio !== null && Math.abs(ratio - lastGalleryRatio) < 0.0008) return;
    lastGalleryRatio = ratio;
    galleryWrap.style.setProperty('aspect-ratio', String(ratio), 'important');
  }

  function setGalleryRatio(index) {
    const slide = gallerySlides[index];
    if (!galleryMain || !slide) return;
    galleryActiveIndex = index;
    updateGalleryCounter(index);
    syncGalleryVideos(index);
    applyGalleryRatioValue(parseFloat(slide.dataset.aspectRatio) || 0);
  }

  // No resize handler needed: aspect-ratio recomputes against the element's
  // own width automatically. The px-height version required one; this does not.

  // Thumbs have no src on first paint. CSS hide + loading=lazy still lets
  // some browsers fetch them and steal LCP bandwidth. Copy data-src only
  // from 768px up (inline script does the same before first desktop paint).
  function hydrateGalleryThumbs() {
    if (!window.matchMedia || !window.matchMedia('(min-width: 768px)').matches) return;
    document.querySelectorAll('#pdp-gallery-thumbs img[data-src]:not([src])').forEach((img) => {
      const srcset = img.getAttribute('data-srcset');
      const sizes = img.getAttribute('data-sizes');
      if (srcset) img.srcset = srcset;
      if (sizes) img.sizes = sizes;
      img.src = img.getAttribute('data-src');
    });
  }

  if (galleryMain && thumbItems.length > 0) {
    function setActiveThumb(index) {
      thumbItems.forEach((t, idx) => {
        const on = idx === index;
        t.classList.toggle('active', on);
        /* aria-current alongside the class so the state is exposed, not just
           painted - the thumbs became <button>s on 23 Aug 2026. */
        if (on) { t.setAttribute('aria-current', 'true'); }
        else { t.removeAttribute('aria-current'); }
      });
      const active = thumbItems[index];
      if (active && typeof active.scrollIntoView === 'function' && window.innerWidth >= 768) {
        active.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      }
    }

    // Track is translated, not overflow-scrolled. On snap, the wrapper
    // aspect-ratio becomes the active photo's ratio so object-fit:contain
    // fills the frame with no crop and no letterbox. Ratio does not change
    // while the finger is down.
    let lastCounterIndex = galleryActiveIndex;
    let drag = null;
    const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function galleryWidth() {
      return (galleryWrap || galleryMain).clientWidth || 1;
    }
    function layoutSlides() {
      const w = galleryWidth();
      gallerySlides.forEach((slide) => {
        slide.style.flex = '0 0 ' + w + 'px';
        slide.style.width = w + 'px';
        slide.style.minWidth = w + 'px';
        slide.style.maxWidth = w + 'px';
      });
    }
    function warmSlide(i) {
      const slide = gallerySlides[i];
      if (!slide) return;
      const img = slide.querySelector('img');
      if (!img) return;
      img.loading = 'eager';
      if (img.complete) return;
      const pre = new Image();
      pre.src = img.currentSrc || img.src;
    }
    function warmAround(i) {
      warmSlide(i);
      warmSlide(i + 1);
      warmSlide(i + 2);
      warmSlide(i - 1);
    }
    function setTrackX(x, animate) {
      galleryMain.style.transition = animate && !reduceMotion
        ? 'transform 420ms cubic-bezier(0.25, 0.8, 0.25, 1)'
        : 'none';
      galleryMain.style.transform = 'translate3d(' + x + 'px,0,0)';
    }
    goToSlide = function (index, animate) {
      const count = gallerySlides.length;
      const target = ((index % count) + count) % count;
      // Arrows and swipes loop at both ends. Jump directly on a wrap instead
      // of animating backwards through the entire strip (potentially 20+ images).
      // Keep actual slides: cloning media would duplicate video/iframe players.
      const wrapped = index < 0 || index >= count;
      const shouldAnimate = animate !== false && !reduceMotion && !wrapped;
      galleryActiveIndex = target;
      lastCounterIndex = target;
      layoutSlides();
      applyGalleryRatioValue(parseFloat(gallerySlides[target].dataset.aspectRatio) || 0);
      updateGalleryCounter(target);
      setActiveThumb(target);
      setTrackX(-target * galleryWidth(), shouldAnimate);
      warmAround(target);
      if (!shouldAnimate) syncGalleryVideos(target);
    };
    galleryMain.addEventListener('transitionend', function (e) {
      if (e.propertyName !== 'transform') return;
      syncGalleryVideos(galleryActiveIndex);
    });

    const dragRoot = galleryWrap || galleryMain;
    function eventPoint(e) {
      const t = (e.touches && e.touches[0]) || (e.changedTouches && e.changedTouches[0]);
      if (t) return { x: t.clientX, y: t.clientY };
      return { x: e.clientX, y: e.clientY };
    }
    function startDrag(e) {
      if (e.target.closest && e.target.closest('.pdp-gallery-nav')) return;
      if (e.touches && e.touches.length > 1) { cancelDrag(); return; }
      layoutSlides();
      const p = eventPoint(e);
      drag = {
        startX: p.x,
        startY: p.y,
        lastX: p.x,
        lastT: performance.now(),
        originX: -galleryActiveIndex * galleryWidth(),
        vel: 0,
        moved: false,
        axis: null
      };
      setTrackX(drag.originX, false);
    }
    function moveDrag(e) {
      if (!drag) return;
      // A second finger belongs to browser zoom, never to carousel navigation.
      if (e.touches && e.touches.length > 1) { cancelDrag(); return; }
      const p = eventPoint(e);
      const dx = p.x - drag.startX;
      const dy = p.y - drag.startY;
      if (!drag.axis) {
        if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
        drag.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
      if (drag.axis === 'y') { drag = null; return; }
      drag.moved = true;
      e.preventDefault();
      const now = performance.now();
      const dt = Math.max(8, now - drag.lastT);
      drag.vel = (p.x - drag.lastX) / dt;
      drag.lastX = p.x;
      drag.lastT = now;
      const w = galleryWidth();
      let x = drag.originX + dx;
      const minX = -(gallerySlides.length - 1) * w;
      if (x > 0) x *= 0.25;
      else if (x < minX) x = minX + (x - minX) * 0.25;
      setTrackX(x, false);
      const idx = Math.max(0, Math.min(gallerySlides.length - 1, Math.round(-x / w)));
      if (idx !== lastCounterIndex) {
        lastCounterIndex = idx;
        updateGalleryCounter(idx);
      }
    }
    function endDrag() {
      if (!drag) return;
      const dx = drag.lastX - drag.startX;
      const vel = drag.vel;
      drag = null;
      let next = galleryActiveIndex;
      const threshold = Math.min(galleryWidth() * 0.2, 72);
      if (dx < -threshold || (dx < -20 && vel < -0.25)) next += 1;
      else if (dx > threshold || (dx > 20 && vel > 0.25)) next -= 1;
      goToSlide(next, true);
    }
    function cancelDrag() {
      if (!drag) return;
      drag = null;
      goToSlide(galleryActiveIndex, false);
    }
    dragRoot.addEventListener('touchstart', startDrag, { passive: true });
    dragRoot.addEventListener('touchmove', moveDrag, { passive: false });
    dragRoot.addEventListener('touchend', endDrag);
    dragRoot.addEventListener('touchcancel', cancelDrag);
    dragRoot.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      startDrag(e);
    });
    dragRoot.addEventListener('pointermove', function (e) {
      if (e.pointerType !== 'mouse') return;
      moveDrag(e);
    });
    dragRoot.addEventListener('pointerup', function (e) {
      if (e.pointerType !== 'mouse') return;
      endDrag();
    });
    dragRoot.addEventListener('pointercancel', cancelDrag);
    window.addEventListener('resize', function () { goToSlide(galleryActiveIndex, false); });
    layoutSlides();
    goToSlide(0, false);
    requestAnimationFrame(function () {
      layoutSlides();
      goToSlide(galleryActiveIndex, false);
    });

    thumbItems.forEach((thumb, idx) => {
      thumb.addEventListener('click', () => goToSlide(idx));
    });

    // Arrow Navigation for Main Gallery. Target index is derived from the
    // tracked galleryActiveIndex, not inferred from scroll position, for the
    // same reason as the thumb clicks above.
    const btnPrev = document.querySelector('.pdp-gallery-prev');
    const btnNext = document.querySelector('.pdp-gallery-next');
    if (btnPrev && btnNext) {
      // Same single path as the thumbnails — see goToSlide above.
      btnPrev.addEventListener('click', () => goToSlide(galleryActiveIndex - 1));
      btnNext.addEventListener('click', () => goToSlide(galleryActiveIndex + 1));
    }

    // Arrow Navigation for Thumbnails
    const thumbGallery = document.getElementById('pdp-gallery-thumbs');
    const thumbPrev = document.querySelector('.pdp-thumb-prev');
    const thumbNext = document.querySelector('.pdp-thumb-next');
    if (thumbGallery && thumbPrev && thumbNext) {
      thumbPrev.addEventListener('click', () => {
        // Scroll up/left by ~2 thumbnails
        const scrollAmount = window.innerWidth >= 768 ? -200 : -100;
        if(window.innerWidth >= 768) {
          thumbGallery.scrollBy({ top: scrollAmount, behavior: 'smooth' });
        } else {
          thumbGallery.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
      });
      thumbNext.addEventListener('click', () => {
        const scrollAmount = window.innerWidth >= 768 ? 200 : 100;
        if(window.innerWidth >= 768) {
          thumbGallery.scrollBy({ top: scrollAmount, behavior: 'smooth' });
        } else {
          thumbGallery.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
      });
    }

    // Force thumbs wrapper to match main image height precisely
    const thumbWrapper = document.querySelector('.pdp-gallery-thumbs-wrapper');
    const mainWrapper = document.querySelector('.pdp-gallery-main-wrapper');
    if (thumbWrapper && mainWrapper) {
      // Last values actually written to the DOM. Every write below is guarded
      // against these, which is what stops the ResizeObserver cycle: the
      // observer watches mainWrapper, but thumbWrapper is its sibling in the
      // same flex row, so writing maxHeight could resize mainWrapper and
      // re-enter this callback. An unchanged value now produces no write at
      // all, so our own mutation can no longer feed the observer.
      let lastMaxHeight = null;
      let lastArrows = null; // true | false | 'mobile'
      let arrowsQueued = false;

      // Reading scrollHeight straight after writing maxHeight would force a
      // synchronous layout, so the arrow measurement stays in a frame callback
      // — coalesced, so several observer fires in one frame measure once.
      const measureArrows = () => {
        arrowsQueued = false;
        if (!(thumbGallery && thumbPrev && thumbNext)) return;
        const needed = thumbGallery.scrollHeight > thumbGallery.clientHeight + 5;
        if (needed === lastArrows) return;
        lastArrows = needed;
        thumbPrev.style.display = needed ? 'flex' : 'none';
        thumbNext.style.display = needed ? 'flex' : 'none';
      };

      const queueArrows = () => {
        if (arrowsQueued) return;
        arrowsQueued = true;
        requestAnimationFrame(measureArrows);
      };

      const syncHeight = () => {
        if (window.innerWidth >= 768) {
          const next = mainWrapper.offsetHeight + 'px';
          if (next !== lastMaxHeight) {
            lastMaxHeight = next;
            thumbWrapper.style.maxHeight = next;
          }
          queueArrows();
        } else if (lastMaxHeight !== 'none') {
          lastMaxHeight = 'none';
          lastArrows = 'mobile';
          thumbWrapper.style.maxHeight = 'none';
          if (thumbPrev && thumbNext) {
            thumbPrev.style.display = '';
            thumbNext.style.display = '';
          }
        }
      };

      // Deferred one frame so the first offsetHeight read happens after the
      // initial layout rather than forcing one during DOMContentLoaded.
      requestAnimationFrame(syncHeight);
      window.addEventListener('resize', syncHeight);

      // Also observe size changes (e.g. image loads)
      if (window.ResizeObserver) {
        new ResizeObserver(syncHeight).observe(mainWrapper);
      }
    }

    syncGalleryVideos(galleryActiveIndex);
    hydrateGalleryThumbs();
    if (window.matchMedia) {
      const thumbMq = window.matchMedia('(min-width: 768px)');
      const onThumbMq = (e) => { if (e.matches) hydrateGalleryThumbs(); };
      if (thumbMq.addEventListener) thumbMq.addEventListener('change', onThumbMq);
      else if (thumbMq.addListener) thumbMq.addListener(onThumbMq);
    }
  } else if (galleryMain && gallerySlides.length) {
    syncGalleryVideos(galleryActiveIndex);
  }

  // Variant Select / Pill Change Listener
  //
  // Two option-picker markups now feed this: <select class="variant-select">
  // (multi-option fallback) and <button class="kp-preg-pill variant-select-pill">
  // (single-option pill picker, introduced for the pregnancy redesign). Both
  // ultimately need the exact same "find matching variant, update everything"
  // logic, so that logic is pulled into one function read fresh from
  // whichever controls are on the page, rather than duplicated per picker
  // type — the next template that wants a pill picker gets this for free
  // just by using the same classes/data attributes, no JS changes needed.
  function getSelectedOptionValues() {
    const optionCount = variants.reduce((count, variant) => Math.max(count, variant.options.length), 0);
    const selected = new Array(optionCount);

    productRoot.querySelectorAll('.variant-select[data-index]').forEach((select) => {
      const index = Number.parseInt(select.dataset.index, 10);
      if (Number.isInteger(index) && index >= 0 && index < optionCount) {
        selected[index] = select.value;
      }
    });

    productRoot.querySelectorAll('.variant-select-pill[aria-pressed="true"][data-index]').forEach((pill) => {
      const index = Number.parseInt(pill.dataset.index, 10);
      if (Number.isInteger(index) && index >= 0 && index < optionCount) {
        selected[index] = pill.dataset.value;
      }
    });

    return selected;
  }

  function applyVariantSelection() {
      // Re-evaluate selected options
      const selectedOptions = getSelectedOptionValues();

      // Match by Shopify option position. Value-only matching is ambiguous when
      // two option dimensions happen to use the same label.
      const matchedVariant = findVariantByOptions(variants, selectedOptions);

      if (matchedVariant) {
        // Update input id
        if (variantIdInput) variantIdInput.value = matchedVariant.id;

        // Update sticky ATC hidden input if it exists
        const stickyIdInput = document.querySelector('.sticky-atc-bar input[name="id"]');
        if (stickyIdInput) stickyIdInput.value = matchedVariant.id;

        // Wallet buttons (buy panel + express sticky) read the form's id
        // on change. Writing .value alone leaves Apple Pay / Shop Pay on
        // the previous colour.
        [variantIdInput, stickyIdInput].forEach((input) => {
          if (input) input.dispatchEvent(new Event('change', { bubbles: true }));
        });

        // Update sticky ATC price display
        const stickyPrice = document.querySelector('.sticky-atc-price');
        if (stickyPrice) stickyPrice.textContent = formatMoney(matchedVariant.price);

        // Keep the colour/size line in step with the picker. Liquid only
        // prints the initial variant; without this, Pick Pink still said
        // White in the sticky bar (the only copy on the bar on mobile).
        const stickyVariant = document.querySelector('.sticky-atc-variant[data-sticky-variant-source="variant"]');
        if (stickyVariant && matchedVariant.title && matchedVariant.title !== 'Default Title') {
          stickyVariant.textContent = matchedVariant.title;
        }

        // Update sticky ATC image if it exists
        const stickyImg = document.querySelector('.sticky-atc-img');
        if (stickyImg && matchedVariant.featured_image && matchedVariant.featured_image.src) {
          // Variant JSON only ever gives the original full-resolution asset.
          // The initial Liquid render sizes this same 56px slot with
          // image_url: width:120 — match that here instead of fetching the
          // uploaded original into a thumbnail on every variant switch.
          // Width only — no height/crop. The thumbnail uses
          // object-fit:contain so it needs the natural aspect ratio; a
          // square centre-crop of a tall studio shot is just background.
          // Must stay in step with the Liquid initial render.
          const rawSrc = matchedVariant.featured_image.src;
          const sep = rawSrc.indexOf('?') === -1 ? '?' : '&';
          stickyImg.src = rawSrc + sep + 'width=200';
        }

        // Update sticky ATC submit button state.
        //
        // Write to the LABEL span, never to the button's own textContent.
        // The button carries two children — the label and the price — and
        // assigning textContent on the button replaces every child node,
        // which silently deleted the price element on the first variant
        // change and left "Add to bag" with no price for the rest of the
        // session. The price is set just above this block, so wiping it
        // here also undid that write.
        const stickySubmitBtn = document.querySelector('.sticky-atc-bar button[name="add"]');
        if (stickySubmitBtn) {
          const stickyLabel = stickySubmitBtn.querySelector('.sticky-atc-cta-label');
          const stickyBtnPrice = stickySubmitBtn.querySelector('.sticky-atc-price');
          const setStickyLabel = (text) => {
            if (stickyLabel) stickyLabel.textContent = text;
            else stickySubmitBtn.textContent = text;
          };
          if (matchedVariant.available) {
            stickySubmitBtn.removeAttribute('disabled');
            if (stickyLabel) {
              if (!stickyLabel.dataset.availableText) stickyLabel.dataset.availableText = stickyLabel.textContent;
              stickyLabel.textContent = stickyLabel.dataset.availableText;
            } else {
              stickySubmitBtn.textContent = productThemeStrings.addToCart;
            }
            if (stickyBtnPrice) stickyBtnPrice.hidden = false;
          } else {
            stickySubmitBtn.setAttribute('disabled', 'true');
            setStickyLabel(productThemeStrings.soldOut);
            // No price claim next to a sold-out label.
            if (stickyBtnPrice) stickyBtnPrice.hidden = true;
          }
        }

        const stickyWallet = document.querySelector('.sticky-atc-wallet');
        if (stickyWallet) stickyWallet.hidden = !matchedVariant.available;

        // Update price
        const isOnSale = matchedVariant.compare_at_price && matchedVariant.compare_at_price > matchedVariant.price;
        priceDisplays.forEach((price) => {
          price.textContent = formatMoney(matchedVariant.price);
        });
        comparePriceDisplays.forEach((price) => {
          price.textContent = isOnSale ? formatMoney(matchedVariant.compare_at_price) : '';
        });
        priceContainer?.classList.toggle('f-price--on-sale', Boolean(isOnSale));

        // Generic attribute-based price hooks, for templates whose markup
        // doesn't use the .pdp-price / .pdp-compare-price class names above
        // (e.g. pregnancy's .kp-preg-price__current) — avoids growing this
        // file's selector list every time a new template ships its own BEM
        // classes.
        document.querySelectorAll('[data-variant-price]').forEach((el) => {
          el.textContent = formatMoney(matchedVariant.price);
        });
        document.querySelectorAll('[data-variant-compare-price]').forEach((el) => {
          el.textContent = isOnSale ? formatMoney(matchedVariant.compare_at_price) : '';
        });
        document.querySelectorAll('[data-variant-cta-price]').forEach((el) => {
          el.textContent = formatMoney(matchedVariant.price);
          el.hidden = !matchedVariant.available;
        });

        // Main add-to-cart button: disable and swap label on an unavailable
        // variant. Templates that render this markup (e.g. pregnancy's real
        // Shopify variants) must not let a shopper submit a sold-out combo.
        if (mainAtcBtn) {
          mainAtcBtn.disabled = !matchedVariant.available;
          const submitLabel = mainAtcBtn.querySelector('[data-variant-label]');
          if (submitLabel) {
            if (!submitLabel.dataset.availableText) submitLabel.dataset.availableText = submitLabel.textContent;
            submitLabel.textContent = matchedVariant.available ? submitLabel.dataset.availableText : productThemeStrings.soldOut;
          }
        }

        // Keep the multi-buy saving in step with the selected variant, so a
        // 50ml -> 200ml switch recalculates the cash figure instead of
        // quoting the previous variant's saving.
        // Re-cap the quantity stepper for the newly selected variant, and pull
        // the current value down if it now exceeds what can be bought.
        const qtyInput = productForm.querySelector('input[name="quantity"]');
        if (qtyInput) {
          const maxQty = matchedVariant.max_qty;
          if (maxQty === null || maxQty === undefined) {
            qtyInput.removeAttribute('max');
            qtyInput.removeAttribute('data-max');
          } else {
            qtyInput.setAttribute('max', maxQty);
            qtyInput.setAttribute('data-max', maxQty);
            if ((parseInt(qtyInput.value, 10) || 1) > maxQty) {
              qtyInput.value = maxQty > 0 ? maxQty : 1;
            }
          }
        }
        const stickyQty = document.querySelector('.sticky-atc-bar [data-sticky-qty]');
        if (stickyQty) stickyQty.value = (qtyInput && qtyInput.value) || '1';

        const bundleNote = document.querySelector('[data-bundle-note]');
        if (bundleNote) {
          const bQty = parseInt(bundleNote.dataset.bundleQty, 10) || 2;
          const bPct = parseFloat(bundleNote.dataset.bundlePct) || 0;
          // Hide the multi-buy offer if this variant can't supply that many.
          const vMax = matchedVariant.max_qty;
          const stockOk = (vMax === null || vMax === undefined) ? true : vMax >= bQty;
          bundleNote.hidden = !(stockOk && matchedVariant.available);
          if (bPct > 0) {
            const total = matchedVariant.price * bQty;
            // Math.floor, not Math.round: the Liquid initial render computes
            // both figures with divided_by, which floors on integers. Round
            // moved the per-unit price by a penny after a variant switch —
            // same class of bug the cart instalment line already fixed.
            const saving = Math.floor(total * bPct / 100);
            // Per-unit discounted price is the hero figure on the tile, so it
            // has to track the variant too — not just the total saving.
            const each = Math.floor(matchedVariant.price * (100 - bPct) / 100);
            const elSaving = bundleNote.querySelector('[data-bundle-saving]');
            if (elSaving) elSaving.textContent = formatMoney(saving);
            const elEach = bundleNote.querySelector('[data-bundle-each]');
            if (elEach) elEach.textContent = formatMoney(each);
            const elWas = bundleNote.querySelector('.pdp-offer__was');
            if (elWas) elWas.textContent = formatMoney(matchedVariant.price);
            const elLabel = bundleNote.querySelector('.pdp-offer__label');
            if (elLabel) elLabel.textContent = 'Buy ' + bQty + ' & save';
          }
        }
        regularPriceGroup?.classList.toggle('hidden', Boolean(isOnSale));
        salePriceGroup?.classList.toggle('hidden', !isOnSale);

        // Update image
        if (matchedVariant.featured_image && matchedVariant.featured_image.src) {
          let matchedIndex = -1;
          thumbItems.forEach((thumb, idx) => {
            const thumbImg = thumb.querySelector('img');
            const thumbSrc = thumb.dataset.src || (thumbImg ? thumbImg.src : '');
            const isMatch = thumbSrc && thumbSrc.includes(matchedVariant.featured_image.src.split('?')[0]);
            thumb.classList.toggle('active', isMatch);
            if (isMatch) matchedIndex = idx;
          });

          if (matchedIndex >= 0) {
            if (galleryMain) {
              goToSlide(matchedIndex);
            }
            /* Removed 23 Aug 2026: this block read an undeclared `viewer`,
               which is a ReferenceError (not undefined), so the && never got
               to short-circuit. It threw on EVERY variant change on EVERY
               product template, aborting the rest of applyVariantSelection():
               history.replaceState never ran so ?variant= was never written
               (share links, bookmarks, refresh and back all reverted to the
               default variant), and the variant:change CustomEvent never
               fired, so theme-analytics variant tracking, cart-drawer's race
               guard and artisan-media's lifestyle/PDF/dimensions swap all
               went dead. Confirmed live before removal:
               "Uncaught ReferenceError: viewer is not defined".
               No viewer/carousel element exists in this theme - the gallery is
               already scrolled above via galleryMain.scrollTo and the thumbs
               are already marked active. Do not reinstate without a real
               element and a null guard. */
          }
        }
        
        // Preserve campaign parameters while exposing the selected variant to
        // Shopify and discount apps that observe History API changes.
        const variantUrl = new URL(window.location.href);
        variantUrl.searchParams.set('variant', matchedVariant.id);
        window.history.replaceState({}, '', variantUrl);

        // Dispatch custom event for app compatibility
        document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: matchedVariant } }));
      } else {
        if (variantIdInput) {
          variantIdInput.value = '';
          variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        const stickyIdInput = document.querySelector('.sticky-atc-bar input[name="id"]');
        if (stickyIdInput) {
          stickyIdInput.value = '';
          stickyIdInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        const stickyWallet = document.querySelector('.sticky-atc-wallet');
        if (stickyWallet) stickyWallet.hidden = true;
        if (mainAtcBtn) {
          mainAtcBtn.disabled = true;
          const submitLabel = mainAtcBtn.querySelector('[data-variant-label]');
          if (submitLabel) submitLabel.textContent = productThemeStrings.unavailable;
        }
        const stickySubmitBtn = document.querySelector('.sticky-atc-bar button[name="add"]');
        if (stickySubmitBtn) {
          stickySubmitBtn.disabled = true;
          // Same reason as the available/sold-out branch above: writing
          // textContent on the button would delete its price child.
          const stickyLabel = stickySubmitBtn.querySelector('.sticky-atc-cta-label');
          const stickyBtnPrice = stickySubmitBtn.querySelector('.sticky-atc-price');
          if (stickyLabel) stickyLabel.textContent = productThemeStrings.unavailable;
          else stickySubmitBtn.textContent = productThemeStrings.unavailable;
          if (stickyBtnPrice) stickyBtnPrice.hidden = true;
        }
      }
  }

  const variantSelects = productRoot.querySelectorAll('.variant-select');
  variantSelects.forEach(select => {
    select.addEventListener('change', () => {
      // Update label text
      const container = select.closest('.variant-selector');
      if (container) {
        const labelVal = container.querySelector('.selected-option-value');
        if (labelVal) labelVal.textContent = select.value;
      }

      applyVariantSelection();
    });
  });

  const variantPills = productRoot.querySelectorAll('.variant-select-pill');
  variantPills.forEach(pill => {
    pill.addEventListener('click', () => {
      if (pill.disabled || pill.getAttribute('aria-disabled') === 'true') return;

      // Only one pill per option group can be pressed at a time.
      const group = pill.closest('[role="group"]') || pill.parentElement;
      const siblings = group ? group.querySelectorAll('.variant-select-pill') : [pill];
      siblings.forEach(sib => sib.setAttribute('aria-pressed', sib === pill ? 'true' : 'false'));

      // Templates with more than one option (e.g. Finish + Mattress) render
      // one label per pill row, each tagged with the same index as its
      // pills' data-index — match by that index rather than grabbing the
      // page's first [data-selected-option-value], which would silently
      // update the wrong row's label whenever more than one option exists.
      const labelVal = productRoot.querySelector('[data-selected-option-value][data-option-index="' + pill.dataset.index + '"]')
        || productRoot.querySelector('[data-selected-option-value]');
      if (labelVal) labelVal.textContent = pill.dataset.value;

      applyVariantSelection();
    });
  });

  function syncStickyQtyFromMain(input) {
    const stickyQty = document.querySelector('.sticky-atc-bar [data-sticky-qty]');
    if (stickyQty) stickyQty.value = (input && input.value) || '1';
  }
  productForm.addEventListener('input', function (e) {
    if (e.target && e.target.name === 'quantity') syncStickyQtyFromMain(e.target);
  });
  productForm.addEventListener('change', function (e) {
    if (e.target && e.target.name === 'quantity') syncStickyQtyFromMain(e.target);
  });

  /* Sticky ATC when the in-page Add is off-screen.

     Hide sticky whenever the in-page Add is on screen. A tall buy panel
     (furniture) used to keep sticky up after the price left the viewport
     even while Add was still visible — two Add buttons at once.

     Express: also hide if in-page Shop Pay is visible.
     When the sticky is already showing, ignore Add that sits behind it. */
  if (stickyAtc && mainAtcBtn) {
    let stickyTicking = false;
    const inViewport = (el, bottomReserve) => {
      if (!el) return false;
      const cs = window.getComputedStyle(el);
      if (cs.display === 'none' || cs.visibility === 'hidden') return false;
      const r = el.getBoundingClientRect();
      const bottom = (typeof bottomReserve === 'number') ? bottomReserve : 8;
      return r.bottom > 8 && r.top < window.innerHeight - bottom && r.width > 0;
    };
    const panelWallet = productForm.querySelector('shopify-accelerated-checkout, .shopify-payment-button');
    const expressSticky = stickyAtc.classList.contains('sticky-atc-bar--express');
    const updateStickyAtc = () => {
      const reserve = stickyAtc.classList.contains('visible')
        ? Math.max(8, stickyAtc.getBoundingClientRect().height + 8)
        : 8;
      const covered = expressSticky
        ? (inViewport(mainAtcBtn, reserve) || inViewport(panelWallet, reserve))
        : inViewport(mainAtcBtn, reserve);
      stickyAtc.classList.toggle('visible', !covered);
      stickyTicking = false;
    };
    const requestStickyUpdate = () => {
      if (stickyTicking) return;
      stickyTicking = true;
      window.requestAnimationFrame(updateStickyAtc);
    };
    updateStickyAtc();
    window.addEventListener('scroll', requestStickyUpdate, { passive: true });
    window.addEventListener('resize', requestStickyUpdate);
    window.addEventListener('load', requestStickyUpdate);
    /* Gallery images / aspect-ratio can push Add below the fold after the
       first measurement. Without this the bar stays hidden until a scroll. */
    if (typeof ResizeObserver === 'function') {
      const stickyLayoutRo = new ResizeObserver(requestStickyUpdate);
      stickyLayoutRo.observe(document.documentElement);
      if (galleryMain) stickyLayoutRo.observe(galleryMain);
    }
    if (typeof IntersectionObserver === 'function') {
      const stickyIo = new IntersectionObserver(requestStickyUpdate, { threshold: 0 });
      stickyIo.observe(mainAtcBtn);
      if (expressSticky && panelWallet) stickyIo.observe(panelWallet);
    }
  }
}

// Same symbols as Liquid `| money` (shop.money_format). Intl + lang=en
// prints A$ on AUD, which disagreed with the PDP price on first paint.
function formatMoney(cents) {
  if (window.KPTheme && typeof KPTheme.formatMoney === 'function') return KPTheme.formatMoney(cents);
  const currency = window.Shopify?.currency?.active || 'GBP';
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
      style: 'currency',
      currency
    }).format(cents / 100);
  } catch (error) {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

// Loox stars click to scroll smoothly to reviews & load instantly
//
// The selector must also cover the per-template buy-panel rating links. Those
// are plain <a href="#looxReviews"> anchors, but #looxReviews does not exist in
// the DOM at load: sections/apps.liquid keeps the Loox app block inside
// <template data-lazy-app-template>, and template content is inert until
// hydrateLazyAppSection() injects it. So the anchor had no target and tapping
// the stars did nothing at all — until the shopper happened to scroll far
// enough for the section to hydrate on its own, after which it worked. That is
// why it looked like an intermittent Loox delay rather than a dead link.
//
// Routing those anchors through this handler hydrates the section first, then
// scrolls, so the first tap works. Reported on iPhone; reproduced from the
// server HTML, where #looxReviews is only ever found inside the <template>.
document.addEventListener('click', function(e) {
  const looxWidget = e.target.closest(
    '.loox-rating-wrapper, .loox-rating, [data-testid="loox-rating-widget"], .loox-rating-content,' +
    'a[href="#looxReviews"], [data-scroll-to="#looxReviews"],' +
    '.kp-put-rating, .kp-preg-rating, .kp-tay-rating, .kp-art-rating,' +
    '.kp-fur-rating, .kp-kc-rating, .kp-nc-rating'
  );
  if (looxWidget) {
    const lazyReviews = document.querySelector('[data-lazy-app-section]');
    if (lazyReviews) window.KPTheme?.hydrateLazyAppSection(lazyReviews);

    e.preventDefault();

    const findReviews = () => document.querySelector(
      '#looxReviews, #looxReviewsFrame, .loox-reviews-default, [data-testid="loox-reviews-iframe"]'
    );

    const scrollToReviews = (node) => {
      if (!node) return false;
      node.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return true;
    };

    if (scrollToReviews(findReviews())) return;

    let tries = 0;
    const timer = window.setInterval(() => {
      tries += 1;
      if (scrollToReviews(findReviews()) || tries > 20) window.clearInterval(timer);
    }, 50);
  }
});

// Native Mobile Media Gallery Swipe & Deferred Image Hydration
function initProductPage(root) {
  initProductDetails(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initProductPage(document), { once: true });
} else {
  initProductPage(document);
}

document.addEventListener('shopify:section:load', (event) => {
  initProductPage(event.target);
});
